import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Trash2, Plus, Image as ImageIcon, Globe, Car, Share2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import ImageDropZone from '@/components/admin/ImageDropZone';

const FALLBACK_LOGO = 'https://media.base44.com/images/public/69e7659143303e8153678f84/d1f090528_Asset1.png';

export default function AdminBrandAssets() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = useCallback(() => {
    try {
      base44.entities.BrandAsset.list()
        .then((recs) => { setAssets(recs || []); setLoading(false); })
        .catch(() => setLoading(false));
    } catch {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const findAsset = (type, simKey) =>
    assets.find((a) => a.asset_type === type && (!simKey || a.sim_key === simKey));

  const upsert = async (data) => {
    setSaving(`${data.asset_type}:${data.sim_key || ''}`);
    try {
      const existing = findAsset(data.asset_type, data.sim_key);
      if (existing) {
        await base44.entities.BrandAsset.update(existing.id, data);
      } else {
        await base44.entities.BrandAsset.create(data);
      }
      load();
    } finally {
      setSaving(null);
    }
  };

  const remove = async (asset) => {
    setSaving(`${asset.asset_type}:${asset.sim_key || ''}`);
    try {
      await base44.entities.BrandAsset.delete(asset.id);
      load();
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const simLogos = assets.filter((a) => a.asset_type === 'sim_logo');

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Brand & Venue Assets</h1>
        <p className="text-white/50 text-sm mt-1">Upload and swap brand logos, sim icons, favicon, and social share card — no code changes needed.</p>
      </div>

      {/* Simulator Logos */}
      <SectionCard icon={Car} title="Simulator Logos" desc="Per-sim logos shown in the RigDetail telemetry badge. Falls back to a monogram (AC/iR) if no logo is uploaded.">
        <div className="space-y-4">
          {simLogos.map((asset) => (
            <SimLogoRow key={asset.id} asset={asset} onSave={upsert} onRemove={remove} saving={saving} />
          ))}
          <AddSimRow onSave={upsert} saving={saving} />
        </div>
      </SectionCard>

      {/* Favicon */}
      <SectionCard icon={Globe} title="Favicon & App Icon" desc="Browser tab icon (.ico / .png). Recommended 32×32px or larger.">
        <SingleAssetRow
          asset={findAsset('favicon')}
          onSave={(file_url) => upsert({ asset_type: 'favicon', file_url, label: 'Favicon' })}
          onRemove={remove}
          saving={saving}
          previewClass="w-8 h-8"
          accept="image/x-icon,image/png,image/svg+xml"
        />
      </SectionCard>

      {/* Main Brand Logo */}
      <SectionCard icon={ImageIcon} title="Main Brand Logo" desc="Primary Motion Reality logo used in the navbar and footer (SVG/PNG). Falls back to the default if not set.">
        <SingleAssetRow
          asset={findAsset('brand_logo')}
          onSave={(file_url) => upsert({ asset_type: 'brand_logo', file_url, label: 'Brand Logo' })}
          onRemove={remove}
          saving={saving}
          previewClass="h-10 w-auto"
          fallback={FALLBACK_LOGO}
        />
      </SectionCard>

      {/* OG Image */}
      <SectionCard icon={Share2} title="Social / Open Graph Preview" desc="1200×630px share card for social media / WhatsApp link unfurling.">
        <SingleAssetRow
          asset={findAsset('og_image')}
          onSave={(file_url) => upsert({ asset_type: 'og_image', file_url, label: 'Open Graph Image' })}
          onRemove={remove}
          saving={saving}
          previewClass="w-full max-w-md aspect-[1200/630]"
        />
      </SectionCard>
    </div>
  );
}

function SectionCard({ icon: Icon, title, desc, children }) {
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-start gap-3">
        <Icon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div>
          <h2 className="text-white font-bold text-sm">{title}</h2>
          <p className="text-white/40 text-xs mt-0.5">{desc}</p>
        </div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function SingleAssetRow({ asset, onSave, onRemove, saving, previewClass, fallback, accept = 'image/*' }) {
  const key = `${asset?.asset_type}:${asset?.sim_key || ''}`;
  const isSaving = saving === key;
  const url = asset?.file_url || fallback;

  return (
    <div className="flex items-start gap-5">
      <div className="w-32 flex flex-col items-center gap-2 shrink-0">
        <div className="w-32 h-16 flex items-center justify-center bg-black/30 border border-white/10 rounded">
          {url ? (
            <img src={url} alt="Preview" className={`${previewClass} object-contain max-h-full max-w-full`} />
          ) : (
            <span className="text-white/20 text-[10px]">No preview</span>
          )}
        </div>
        {asset && (
          <button
            onClick={() => onRemove(asset)}
            disabled={isSaving}
            className="flex items-center gap-1 text-red-400/60 hover:text-red-400 text-[10px] font-bold tracking-wider uppercase transition-colors"
          >
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        )}
      </div>
      <div className="flex-1">
        <ImageDropZone
          onUploaded={(file_url) => onSave(file_url)}
          multiple={false}
          label="DROP FILE OR CLICK TO BROWSE"
          compact
        />
        {isSaving && <p className="text-primary text-[10px] mt-1.5 font-bold">Saving…</p>}
      </div>
    </div>
  );
}

function SimLogoRow({ asset, onSave, onRemove, saving }) {
  const isSaving = saving === `${asset.asset_type}:${asset.sim_key}`;

  return (
    <div className="flex items-start gap-4 border border-white/10 rounded p-3">
      <div className="w-20 flex flex-col items-center gap-2 shrink-0">
        <div className="w-20 h-10 flex items-center justify-center bg-black/30 border border-white/10 rounded">
          <img src={asset.file_url} alt={asset.label} className="h-5 w-auto object-contain max-w-full" />
        </div>
        <button
          onClick={() => onRemove(asset)}
          disabled={isSaving}
          className="flex items-center gap-1 text-red-400/60 hover:text-red-400 text-[10px] font-bold tracking-wider uppercase"
        >
          <Trash2 className="w-3 h-3" /> Remove
        </button>
      </div>
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="text-white/40 text-[10px] font-bold tracking-wider uppercase">Sim Key</label>
            <input
              value={asset.sim_key}
              readOnly
              className="w-full bg-black/30 border border-white/10 text-white/60 text-xs px-2 py-1 rounded font-mono mt-0.5"
            />
          </div>
          <div className="flex-1">
            <label className="text-white/40 text-[10px] font-bold tracking-wider uppercase">Label</label>
            <input
              value={asset.label}
              readOnly
              className="w-full bg-black/30 border border-white/10 text-white/60 text-xs px-2 py-1 rounded mt-0.5"
            />
          </div>
        </div>
        <ImageDropZone
          onUploaded={(file_url) => onSave({ ...asset, file_url })}
          multiple={false}
          label="REPLACE LOGO"
          compact
        />
      </div>
    </div>
  );
}

function AddSimRow({ onSave, saving }) {
  const [adding, setAdding] = useState(false);
  const [simKey, setSimKey] = useState('');
  const [label, setLabel] = useState('');

  const handleSave = (file_url) => {
    if (!simKey.trim()) return;
    onSave({ asset_type: 'sim_logo', sim_key: simKey.trim(), file_url, label: label.trim() || simKey.trim() });
    setAdding(false);
    setSimKey('');
    setLabel('');
  };

  if (!adding) {
    return (
      <button
        onClick={() => setAdding(true)}
        className="flex items-center gap-1.5 text-primary hover:text-primary/80 text-xs font-bold tracking-wider uppercase transition-colors"
      >
        <Plus className="w-3.5 h-3.5" /> Add Sim
      </button>
    );
  }

  return (
    <div className="border border-primary/30 rounded p-3 space-y-2">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <label className="text-white/40 text-[10px] font-bold tracking-wider uppercase">Sim Key</label>
          <input
            value={simKey}
            onChange={(e) => setSimKey(e.target.value)}
            placeholder="e.g. automobilista_2"
            className="w-full bg-black/30 border border-white/10 text-white text-xs px-2 py-1 rounded font-mono mt-0.5 focus:border-primary/50"
          />
        </div>
        <div className="flex-1">
          <label className="text-white/40 text-[10px] font-bold tracking-wider uppercase">Label</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Automobilista 2"
            className="w-full bg-black/30 border border-white/10 text-white text-xs px-2 py-1 rounded mt-0.5 focus:border-primary/50"
          />
        </div>
        <button
          onClick={() => setAdding(false)}
          className="text-white/40 hover:text-white text-[10px] font-bold tracking-wider uppercase mt-4"
        >
          Cancel
        </button>
      </div>
      <ImageDropZone
        onUploaded={handleSave}
        multiple={false}
        label="UPLOAD LOGO"
        compact
      />
      {!simKey.trim() && <p className="text-amber-400/60 text-[10px]">Enter a sim key before uploading</p>}
    </div>
  );
}